import { useState, useEffect } from 'react';
import { MasterFlightRecord } from '../../../../../../../utils/types';

type UpdateDraftFn = <K extends keyof MasterFlightRecord>(
  key: K,
  value: MasterFlightRecord[K]
) => void;

/**
 * Custom hook for managing split flight number input
 * Handles prefix (auto-filled from flight owner) and digits (manual input)
 */
export function useFlightNumber(draft: MasterFlightRecord, updateDraft: UpdateDraftFn) {
  // Extract numeric part of flight number (e.g., "MH 0794" -> "0794")
  const getFlightNumberNumericPart = (): string => {
    if (!draft.flightNo) return '';
    const parts = draft.flightNo.trim().split(/\s+/);
    return parts.length > 1 ? parts[parts.length - 1] : draft.flightNo.replace(/[^0-9]/g, '');
  };

  const [flightNumberDigits, setFlightNumberDigits] = useState(getFlightNumberNumericPart());

  // Sync flightNumberDigits when draft.flightNo changes externally (e.g., when editing existing record)
  // Only sync when flightNo contains a space (i.e., "PREFIX DIGITS" format),
  // otherwise we can't distinguish a numeric prefix from digits
  useEffect(() => {
    const flightNo = draft.flightNo?.trim() || '';
    if (!flightNo.includes(' ')) return;
    const numericPart = getFlightNumberNumericPart();
    if (numericPart !== flightNumberDigits) {
      setFlightNumberDigits(numericPart);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.flightNo]);

  // Handle flight number digits input (numbers only, max 4 digits)
  const handleFlightNumberDigitsChange = (value: string) => {
    // Filter to only allow digits, max 4 characters
    const filtered = value.replace(/[^0-9]/g, '').slice(0, 4);
    setFlightNumberDigits(filtered);

    // Combine with flight owner to create full flight number
    const fullFlightNo = draft.flightOwner ? `${draft.flightOwner} ${filtered}` : filtered;
    updateDraft('flightNo', fullFlightNo);
  };

  return {
    flightNumberDigits,
    handleFlightNumberDigitsChange,
  };
}
